import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchResourceManagerQueue,
  fetchResourceManagerQueueFilters,
  assignEngagementManager,
  fetchOrderDetail,
} from "../services/resourceManager.service";
import { upsertServiceDetails } from "./engagementManagerSlice";
import { fetchAllPages } from "../../util/helpers";

const initialState = {
  rows: [],
  totalRecords: 0,
  pageNumber: 1,
  pageSize: 10,
  loading: false,
  error: null,

  filterOptions: {
    superRegions: [],
    countryRegions: [],
    offices: [],
    sectors: [],
    statuses: [],
    clientNames: [],
  },
  filterOptionsLoading: false,
  filterOptionsError: null,

  assignLoading: false,
  assignError: null,

  orderDetail: null,
  orderDetailLoading: false,
  orderDetailError: null,

};

function toArrayOrNull(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr;
}

function toDateString(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

export const fetchQueue = createAsyncThunk(
  "resourceManager/fetchQueue",
  async ({ domain, ...filters }, { rejectWithValue }) => {
    try {
      const dto = {
        pageNumber: filters.pageNumber || 1,
        pageSize: filters.pageSize || 10,
        search: filters.search || null,
        superRegion: toArrayOrNull(filters.superRegion),
        countryRegion: toArrayOrNull(filters.countryRegion),
        office: toArrayOrNull(filters.office),
        sector: toArrayOrNull(filters.sector),
        status: toArrayOrNull(filters.status),
        clientName: toArrayOrNull(filters.clientName),
        startDate: toDateString(filters.date?.[0]) || null,
        endDate: toDateString(filters.date?.[1]) || null,
        loggedInUserEmail: filters.loggedInUserEmail || null,
        processingView: filters.processingView ?? null,
      };
      return await fetchAllPages({
        fetchPage: ({ pageNumber, pageSize }) =>
          fetchResourceManagerQueue({ ...dto, pageNumber, pageSize }, domain),
        getRows: (res) => res?.data?.data,
        getTotal: (res) => res?.data?.totalRecords,
        withRows: (res, rows) => ({ ...res, data: { ...res.data, data: rows } }),
      });
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchFilters = createAsyncThunk(
  "resourceManager/fetchFilters",
  async (
    { domain, loggedInUserEmail, status, superRegion,processingView } = {},
    { rejectWithValue },
  ) => {
    try {
      return await fetchResourceManagerQueueFilters(
        {
          loggedInUserEmail: loggedInUserEmail || null,
          status: status || null,
          superRegion: superRegion ? (Array.isArray(superRegion) ? superRegion : [superRegion]) : null,
          processingView: processingView ?? null,
        },
        domain,
      );
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const assignEM = createAsyncThunk(
  "resourceManager/assignEM",
  async ({ requestIds, engagementManagerEmail, firstName, lastName, actingUserEmail, domain }, { rejectWithValue }) => {
    try {
      return await assignEngagementManager({
        requestIds,
        engagementManagerEmail,
        firstName,
        lastName,
        actingUserEmail,
        domain,
      });
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const fetchOrderDetails = createAsyncThunk(
  "resourceManager/fetchOrderDetails",
  async ({ requestId, domain, engagementManagerMailId }, { rejectWithValue }) => {
    try {
      return await fetchOrderDetail(requestId, domain, engagementManagerMailId);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

const resourceManagerSlice = createSlice({
  name: "resourceManager",
  initialState,
  reducers: {
    resetAssignError: (state) => {
      state.assignError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchQueue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQueue.fulfilled, (state, action) => {
        state.loading = false;
        const paged = action.payload?.data;
        state.rows = paged?.data || [];
        state.totalRecords = paged?.totalRecords || 0;
        state.pageNumber = paged?.pageNumber || 1;
        state.pageSize = paged?.pageSize || 10;
      })
      .addCase(fetchQueue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchFilters.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchFilters.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        const opts = action.payload?.data || action.payload;
        state.filterOptions = {
          superRegions: opts?.superRegions || [],
          countryRegions: opts?.countryRegions || [],
          offices: opts?.offices || [],
          sectors: opts?.sectors || [],
          statuses: opts?.statuses || [],
          clientNames: opts?.clientNames || [],
        };
      })
      .addCase(fetchFilters.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload;
      });

    builder
      .addCase(assignEM.pending, (state) => {
        state.assignLoading = true;
        state.assignError = null;
      })
      .addCase(assignEM.fulfilled, (state) => {
        state.assignLoading = false;
      })
      .addCase(assignEM.rejected, (state, action) => {
        state.assignLoading = false;
        state.assignError = action.payload;
      });

    builder
      .addCase(fetchOrderDetails.pending, (state) => {
        state.orderDetailLoading = true;
        state.orderDetailError = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.orderDetailLoading = false;
        const details = action.payload?.data;
        state.orderDetail = Array.isArray(details)
          ? details[0] || null
          : details || null;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.orderDetailLoading = false;
        state.orderDetailError = action.payload;
      });

    builder
      .addCase(upsertServiceDetails.fulfilled, (state, action) => {
        const result = action.payload?.data || action.payload;
        if (state.orderDetail?.serviceDetails && result) {
          if (Array.isArray(result.engagementDelegates)) {
            state.orderDetail.serviceDetails.engagementDelegates = result.engagementDelegates;
          }
          if (result.revisedStartDate !== undefined) {
            state.orderDetail.serviceDetails.revisedStartDate = result.revisedStartDate;
          }
          if (result.revisedEndDate !== undefined) {
            state.orderDetail.serviceDetails.revisedEndDate = result.revisedEndDate;
          }
        }
      });
  },
});

export const { resetAssignError } = resourceManagerSlice.actions;

export default resourceManagerSlice.reducer;
